import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ReactFlow, Controls, ReactFlowProvider } from "@xyflow/react";
import Logo from "./Logo";
import StickyNoteNode from "./StickyNoteNode";
import { useTranslation } from "react-i18next";
import TradingGraph from "@/components/charts/TradingGraph";
import {
  useGetCheckForex,
  useGetStrategyExplaination,
} from "@/api/strategyApi";
import { NodeSpotlightOverlay } from "@/components/spotlight/NodeSpotlightOverlay";
import { FaLightbulb } from "react-icons/fa";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import PremiumAnimatedEdge from "@/components/blocks/flow/ButtonEdge";
import "@xyflow/react/dist/style.css";
import CustomSelectorNode from "@/components/blocks/flow/CustomSelectorNode";
import { migrateNodeData, transformNode } from "./canvasFunctions";

const FlowContext = createContext<
  { edges: any[]; nodes: any[]; showValues: boolean } | undefined
>(undefined);

const useFlowContext = () => {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error("useFlowContext must be used within a FlowProvider");
  }
  return context;
};

const FlowProvider = ({
  children,
  edges,
  nodes,
  showValues,
}: {
  children: React.ReactNode;
  edges: any[];
  nodes: any[];
  showValues: boolean;
}) => {
  return (
    <FlowContext.Provider value={{ edges, nodes, showValues }}>
      {children}
    </FlowContext.Provider>
  );
};

const edgeTypes = {
  buttonedge: PremiumAnimatedEdge,
};

const nodeTypes = {
  selectorNode: CustomSelectorNode,
  stickyNoteNode: StickyNoteNode,
};

const ViewModalProvider = ({
  isOpen,
  onRequestClose,
  selected,
  showValues = false,
}: any) => {
  const { t } = useTranslation("common-page");
  const {
    data: explainData,
    isSuccess,
    isLoading,
  } = useGetStrategyExplaination(selected?.strategy_id);
  const { details } = useAppDetails();
  const nodeHints = useMemo(() => {
    if (!isSuccess || !explainData) return [];
    return Object.entries(explainData?.data).map(([key, hint]) => ({
      id: key.split("**").filter(Boolean),
      hint: hint as string,
    }));
  }, [isSuccess, explainData]);

  const [isExplainerActive, setIsExplainerActive] = useState(false);
  const hasHints = nodeHints.length > 0;

  const exitExplainer = () => {
    setIsExplainerActive(false);
  };
  const nodes = selected?.nodes?.map((node: any) => ({
    ...node,
    data: migrateNodeData(node?.data),
  }));
  // Automatically start explainer when hints are ready
  useEffect(() => {
    if (isOpen && hasHints && !isExplainerActive) {
      setTimeout(() => setIsExplainerActive(true), 800);
    }
  }, [isOpen, hasHints, explainData]);

  return (
    <FlowProvider edges={selected.edges} nodes={nodes} showValues={showValues}>
      <Dialog
        open={isOpen}
        onOpenChange={() => {
          onRequestClose();
        }}
      >
        <DialogContent
          className="max-w-[98vw] w-full h-[95vh] flex flex-col p-3 sm:p-6"
        >
          <DialogHeader>
            <DialogTitle>{t("Preview")}</DialogTitle>
          </DialogHeader>

          <div
            id="react-flow-container-share"
            className="relative  rounded-2xl border-2 border-muted-foreground/5 flex-1 overflow-hidden  "
          >
            {isExplainerActive && hasHints && (
              <NodeSpotlightOverlay
                hints={nodeHints ?? []}
                isOpen={isOpen}
                onFinish={exitExplainer}
              />
            )}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[4%] text-accent" />
            {!details?.isWhiteLabled && (
              <div className="absolute z-[-2] bottom-7 text-accent left-0 right-0 pointer-events-none">
                <div className="flex flex-col justify-between items-center h-full">
                  <Logo className="w-[300px] sm:w-[500px] md:w-[600px] max-h-[150px] sm:max-h-[220px] px-6 sm:px-24 text-accent/20" />
                </div>
              </div>
            )}

            <ReactFlow
              nodes={nodes}
              edges={selected.edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              style={{ zIndex: "revert", overflow: "hidden" }}
              className="main__diagram"
              minZoom={0.03}
              fitViewOptions={{ duration: 1000, maxZoom: 1 }}
              fitView
              defaultEdgeOptions={{ animated: true }}
            >
              <Controls className="!bottom-12" />
            </ReactFlow>

            {
              <div className="absolute top-0 left-0 w-full h-full z-[-1]">
                <TradingGraph
                  symbol={selected?.symbol}
                  timeframe={selected?.timeframe}
                  disableInteraction
                  canvas_data={JSON.stringify(selected?.nodes)}
                  broker_id={
                    selected?.broker_id ||
                    (details?.isWhiteLabled ? details?.id : undefined)
                  }
                />
              </div>
            }
          </div>

          <DialogFooter className="flex flex-col sm:flex-row justify-between items-center w-full mt-4 gap-3 sm:gap-2">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {!isExplainerActive && hasHints && (
                <Button
                  variant="outline"
                  onClick={() => setIsExplainerActive(true)}
                  className="w-full sm:w-auto"
                >
                  Start Explainer <FaLightbulb />
                </Button>
              )}
              {isLoading && (
                <Button
                  loadingText={isLoading}
                  variant="ghost"
                  onClick={() => setIsExplainerActive(true)}
                  className="w-full sm:w-auto"
                ></Button>
              )}

              <Button
                variant="outline"
                onClick={onRequestClose}
                className="w-full sm:w-auto"
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FlowProvider>
  );
};

const ViewModal = (props: any) => {
  const { data: response } = useGetCheckForex(props?.selected?.symbol);
  const isForex = response?.data; // ✅ ensure you extract the boolean
  return (
    <ReactFlowProvider>
      <ForexContext.Provider value={{ isForex }}>
        <ViewModalProvider {...props} />
      </ForexContext.Provider>
    </ReactFlowProvider>
  );
};
export default ViewModal;
export { useFlowContext };

export const ForexContext = createContext<
  { isForex: boolean | undefined } | undefined
>(undefined);

export const useForex = () => {
  const context = useContext(ForexContext);
  if (!context) throw new Error("useForex must be used within a ForexProvider");
  return context;
};
